/// <amd-module name="Repos/Changes/Components/Diff/SideBySideDiff" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { FileDiffBlockChangeType, IFileDiff } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext } from "Repos/Common/Contracts/Git";
import { DiffViewerSide } from "Repos/Common/Contracts/VersionControlViewModel";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Splitter, SplitterElementPosition } from "VSSUI/Splitter";
import {
    EllipsisLine,
    EmptyLines,
    IDiffSummaryDiscussionWidgetProvider,
    maxLinesRendered,
    processCharDiffChanges,
    processLineDiffChanges
} from "./DiffBlock";
import { DiffViewerSideContext } from "./DiffContext";

interface ISideBySideDiffScrollProps {
    sourceElement: HTMLElement | null;
    sourceScrolling: boolean[];
    targetElement: HTMLElement | null;
    targetScrolling: boolean[];
}

const onSideScroll = (props: ISideBySideDiffScrollProps): void => {
    const { sourceElement, sourceScrolling, targetElement, targetScrolling } = props;

    // Using a stack to prevent scroll event looping
    if (!sourceScrolling.pop()) {
        if (targetElement && sourceElement) {
            targetScrolling.push(true);

            // Store previous scroll value
            const previousTop = targetElement.scrollTop;
            const previousLeft = targetElement.scrollLeft;

            // Set new scroll position
            targetElement.scrollTop = sourceElement.scrollTop;
            targetElement.scrollLeft = sourceElement.scrollLeft;

            // If the scroll bar didn't move, then the event didn't fire so manually pop
            if (previousTop == targetElement.scrollTop && previousLeft == targetElement.scrollLeft) {
                targetScrolling.pop();
            }
        }
    }
};

export interface ISideBySideDiffProps {
    commentPositions?: CommentThreadContext[];
    fileDiff: IFileDiff;
    widgetProvider?: IDiffSummaryDiscussionWidgetProvider;
}

export class SideBySideDiff extends VssComponent<ISideBySideDiffProps> {
    private originalScrolling: boolean[];
    private modifiedScrolling: boolean[];
    private originalElement: HTMLDivElement | null;
    private modifiedElement: HTMLDivElement | null;

    constructor(props: ISideBySideDiffProps, context: IVssComponentContext) {
        super(props, context);

        this.originalScrolling = [];
        this.modifiedScrolling = [];
    }

    public render(): JSX.Element {
        const { commentPositions, fileDiff, widgetProvider } = this.props;
        const originalDiffBlocks: JSX.Element[] = [];
        const modifiedDiffBlocks: JSX.Element[] = [];
        let contentTruncated = false;
        let ellipsisLinesRendered = 0;
        let linesRendered = 0;
        let originalFileChanges = false;
        let modifiedFileChanges = false;

        if (fileDiff && !fileDiff.imageComparison && !fileDiff.binaryContent && !fileDiff.whitespaceChangesOnly) {
            for (let i = 0; i < fileDiff.lineCharBlocks.length; i++) {
                const currentLinesRendered = linesRendered;
                const currentDisplayLine = currentLinesRendered - ellipsisLinesRendered;
                const diffSection = fileDiff.lineCharBlocks[i];
                const fileName = fileDiff.modifiedFile?.contentMetadata.fileName || fileDiff.originalFile?.contentMetadata.fileName || "";

                if (i === 0 && diffSection.lineChange.truncatedBefore) {
                    originalDiffBlocks.push(
                        <EllipsisLine key={`ellipsis-lines-${i}`} mLinesExist={false} oLinesExist={true} widgetProvider={widgetProvider} />
                    );
                    modifiedDiffBlocks.push(
                        <EllipsisLine key={`ellipsis-lines-${i}`} mLinesExist={true} oLinesExist={false} widgetProvider={widgetProvider} />
                    );
                    linesRendered++;
                    ellipsisLinesRendered++;
                }

                if (currentLinesRendered < maxLinesRendered) {
                    const originalResult: JSX.Element[] = [];
                    const modifiedResult: JSX.Element[] = [];

                    const lineChanges = diffSection.lineChange;

                    // Loop through all changes for this diff block.  This can be an Add/Delete/Edit/None.
                    // Depending on the type, add to appropriate diff and possibly inline character highlighting for edits.
                    if (lineChanges.changeType === FileDiffBlockChangeType.None) {
                        originalFileChanges = true;
                        modifiedFileChanges = true;
                        originalResult.push(
                            ...processLineDiffChanges(
                                fileName,
                                linesRendered,
                                currentDisplayLine,
                                lineChanges.oLines,
                                lineChanges.oLine,
                                0,
                                "unchanged",
                                true,
                                false,
                                widgetProvider,
                                commentPositions
                            )
                        );
                        modifiedResult.push(
                            ...processLineDiffChanges(
                                fileName,
                                linesRendered,
                                currentDisplayLine,
                                lineChanges.mLines,
                                0,
                                lineChanges.mLine,
                                "unchanged",
                                false,
                                true,
                                widgetProvider,
                                commentPositions
                            )
                        );
                        linesRendered += diffSection.lineChange.mLines.length;
                    } else if (diffSection.lineChange.changeType === FileDiffBlockChangeType.Add) {
                        modifiedFileChanges = true;
                        originalResult.push(
                            <EmptyLines
                                fileName={fileName}
                                displayLineNumber={currentDisplayLine}
                                key={`empty-lines-${i}`}
                                lines={lineChanges.mLines.length}
                                oLinesExist={false}
                                mLinesExist={true}
                                widgetProvider={widgetProvider}
                            />
                        );
                        modifiedResult.push(
                            ...processLineDiffChanges(
                                fileName,
                                linesRendered,
                                currentDisplayLine,
                                lineChanges.mLines,
                                undefined,
                                lineChanges.mLine,
                                "added",
                                false,
                                true,
                                widgetProvider,
                                commentPositions
                            )
                        );
                        linesRendered += diffSection.lineChange.mLines.length;
                    } else if (diffSection.lineChange.changeType === FileDiffBlockChangeType.Delete) {
                        originalFileChanges = true;
                        modifiedResult.push(
                            <EmptyLines
                                fileName={fileName}
                                displayLineNumber={currentDisplayLine}
                                key={`empty-lines-${i}`}
                                lines={lineChanges.oLines.length}
                                oLinesExist={true}
                                mLinesExist={false}
                                widgetProvider={widgetProvider}
                            />
                        );
                        originalResult.push(
                            ...processLineDiffChanges(
                                fileName,
                                linesRendered,
                                currentDisplayLine,
                                lineChanges.oLines,
                                lineChanges.oLine,
                                undefined,
                                "removed",
                                true,
                                false,
                                widgetProvider,
                                commentPositions
                            )
                        );
                        linesRendered += diffSection.lineChange.oLines.length;
                    } else if (diffSection.lineChange.changeType === FileDiffBlockChangeType.Edit) {
                        originalFileChanges = true;
                        modifiedFileChanges = true;

                        const originalCharResults = processCharDiffChanges(
                            fileName,
                            FileDiffBlockChangeType.Delete,
                            currentLinesRendered,
                            currentDisplayLine,
                            diffSection,
                            lineChanges.oLines,
                            diffSection.lineChange.oLine,
                            undefined,
                            "removed",
                            true,
                            false,
                            widgetProvider,
                            commentPositions
                        );
                        originalResult.push(...originalCharResults);

                        const modifiedCharResults = processCharDiffChanges(
                            fileName,
                            FileDiffBlockChangeType.Add,
                            currentLinesRendered,
                            currentDisplayLine,
                            diffSection,
                            lineChanges.mLines,
                            undefined,
                            diffSection.lineChange.mLine,
                            "added",
                            false,
                            true,
                            widgetProvider,
                            commentPositions
                        );
                        modifiedResult.push(...modifiedCharResults);

                        linesRendered += Math.min(originalCharResults.length, modifiedCharResults.length);

                        if (modifiedCharResults.length > originalCharResults.length) {
                            const emptyLinesCount = modifiedResult.length - originalCharResults.length;
                            originalResult.push(
                                <EmptyLines
                                    fileName={fileName}
                                    displayLineNumber={currentDisplayLine + originalCharResults.length}
                                    key={`empty-lines-${i}`}
                                    lines={emptyLinesCount}
                                    oLinesExist={true}
                                    mLinesExist={false}
                                    widgetProvider={widgetProvider}
                                />
                            );
                            linesRendered += emptyLinesCount;
                        } else if (originalCharResults.length > modifiedCharResults.length) {
                            const emptyLinesCount = originalCharResults.length - modifiedCharResults.length;
                            modifiedResult.push(
                                <EmptyLines
                                    fileName={fileName}
                                    displayLineNumber={currentDisplayLine + modifiedCharResults.length}
                                    key={`empty-lines-${i}`}
                                    lines={emptyLinesCount}
                                    oLinesExist={false}
                                    mLinesExist={true}
                                    widgetProvider={widgetProvider}
                                />
                            );
                            linesRendered += emptyLinesCount;
                        }

                        if (linesRendered > maxLinesRendered) {
                            linesRendered = maxLinesRendered;
                        }
                    }

                    contentTruncated = linesRendered >= maxLinesRendered;

                    const ellipsisLineAdded = i > 0 && diffSection.lineChange.truncatedAfter;
                    if (ellipsisLineAdded) {
                        linesRendered++;
                        ellipsisLinesRendered++;
                    }

                    originalDiffBlocks.push(
                        <React.Fragment key={"diff-block-container" + i}>
                            <React.Fragment key={"diff-block-section" + i}>{...originalResult}</React.Fragment>
                            {ellipsisLineAdded && <EllipsisLine mLinesExist={false} oLinesExist={true} widgetProvider={widgetProvider} />}
                        </React.Fragment>
                    );

                    modifiedDiffBlocks.push(
                        <React.Fragment key={"diff-block-container" + i}>
                            <React.Fragment key={"diff-block-section" + i}>{...modifiedResult}</React.Fragment>
                            {ellipsisLineAdded && <EllipsisLine mLinesExist={true} oLinesExist={false} widgetProvider={widgetProvider} />}
                        </React.Fragment>
                    );
                } else {
                    break;
                }
            }
        }

        return (
            <>
                {contentTruncated ? (
                    <MessageCard className={"margin-bottom-8"} severity={MessageCardSeverity.Warning} messageBarClassName="repos-summary-message">
                        {Resources.PartialDiffTruncated}
                    </MessageCard>
                ) : null}
                {modifiedFileChanges && !originalFileChanges && (
                    <DiffViewerSideContext.Provider value={{ side: DiffViewerSide.Modified }}>
                        <div className="repos-summary-diff-container body-s">
                            <div>{...modifiedDiffBlocks}</div>
                        </div>
                    </DiffViewerSideContext.Provider>
                )}
                {originalFileChanges && !modifiedFileChanges && (
                    <DiffViewerSideContext.Provider value={{ side: DiffViewerSide.Original }}>
                        <div className="repos-summary-diff-container body-s">
                            <div>{...originalDiffBlocks}</div>
                        </div>
                    </DiffViewerSideContext.Provider>
                )}
                {originalFileChanges && modifiedFileChanges && (
                    <Splitter
                        className="repos-summary-diff-splitter"
                        farElementClassName="flex-column"
                        fixedElement={SplitterElementPosition.Near}
                        key={"commit-splitter"}
                        nearElementClassName="flex-column"
                        onRenderFarElement={() => {
                            return (
                                <div
                                    className="repos-summary-diff-container body-s"
                                    ref={element => (this.originalElement = element)}
                                    onScroll={this.onModifiedScroll}
                                >
                                    <DiffViewerSideContext.Provider value={{ side: DiffViewerSide.Modified }}>
                                        <FocusZone direction={FocusZoneDirection.Vertical} focusGroupProps={{ defaultElementId: "m-diff-widget-0" }}>
                                            <div className="repos-summary-diff-blocks" role="table">
                                                {...modifiedDiffBlocks}
                                            </div>
                                        </FocusZone>
                                    </DiffViewerSideContext.Provider>
                                </div>
                            );
                        }}
                        onRenderNearElement={() => {
                            return (
                                <div
                                    className="repos-summary-diff-container body-s"
                                    ref={element => (this.modifiedElement = element)}
                                    onScroll={this.onOriginalScroll}
                                >
                                    <DiffViewerSideContext.Provider value={{ side: DiffViewerSide.Original }}>
                                        <FocusZone direction={FocusZoneDirection.Vertical} focusGroupProps={{ defaultElementId: "o-diff-widget-0" }}>
                                            <div className="repos-summary-diff-blocks" role="table">
                                                {...originalDiffBlocks}
                                            </div>
                                        </FocusZone>
                                    </DiffViewerSideContext.Provider>
                                </div>
                            );
                        }}
                    />
                )}
            </>
        );
    }

    private onModifiedScroll = (): void => {
        onSideScroll({
            sourceElement: this.originalElement,
            sourceScrolling: this.originalScrolling,
            targetElement: this.modifiedElement,
            targetScrolling: this.modifiedScrolling
        });
    };

    private onOriginalScroll = (): void => {
        onSideScroll({
            sourceElement: this.modifiedElement,
            sourceScrolling: this.modifiedScrolling,
            targetElement: this.originalElement,
            targetScrolling: this.originalScrolling
        });
    };
}
